/**
 * Where the step card sits beside a spotlighted rectangle (all numbers are
 * viewport pixels). Preference: below, above, right, left; the first side the
 * whole card fits on, kept `margin` inside the viewport and centred on the
 * target along the other axis. When no side has room (a target that fills the
 * screen) the card sits over the bottom of the viewport and says so with
 * `side: 'over'`, so the caller can add a shadow-free outline if it likes.
 *
 * @param {{ top: number, left: number, width: number, height: number }} rect
 * @param {{ width: number, height: number }} card
 * @param {{ width: number, height: number }} viewport
 * @returns {{ top: number, left: number, side: 'bottom'|'top'|'right'|'left'|'over' }}
 */
export function placeCard(rect, card, viewport, { gap = 12, margin = 12 } = {}) {
	const clamp = (value, low, high) => Math.max(low, Math.min(value, Math.max(low, high)));
	const bottom = rect.top + rect.height;
	const right = rect.left + rect.width;
	const centredLeft = clamp(rect.left + rect.width / 2 - card.width / 2, margin, viewport.width - margin - card.width);
	const centredTop = clamp(rect.top + rect.height / 2 - card.height / 2, margin, viewport.height - margin - card.height);

	if (bottom + gap + card.height <= viewport.height - margin) return { top: bottom + gap, left: centredLeft, side: 'bottom' };
	if (rect.top - gap - card.height >= margin) return { top: rect.top - gap - card.height, left: centredLeft, side: 'top' };
	if (right + gap + card.width <= viewport.width - margin) return { top: centredTop, left: right + gap, side: 'right' };
	if (rect.left - gap - card.width >= margin) return { top: centredTop, left: rect.left - gap - card.width, side: 'left' };
	return { top: clamp(viewport.height - margin - card.height, margin, viewport.height), left: centredLeft, side: 'over' };
}

/** A rectangle grown by `by` on every side and cut to the viewport. */
export function inflate(rect, by, viewport) {
	const top = Math.max(0, rect.top - by);
	const left = Math.max(0, rect.left - by);
	const bottom = Math.min(viewport.height, rect.top + rect.height + by);
	const right = Math.min(viewport.width, rect.left + rect.width + by);
	return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}
